import { useCallback, useEffect, useState } from 'react';

import { makeAuthenticatedApiCall } from '../../../apiHelper';

/**
 * Fiche agence : vue 360 d'une organisation, agrégée côté backend.
 *
 * Une agence Skipcar vit sur six magasins (Firebase Auth, Firestore, Pipedrive,
 * les miroirs MySQL, les tables métier, Qonto). L'écran existe pour les montrer
 * ENSEMBLE avant tout changement d'entité juridique.
 */

export interface AgencyAttachmentCounts {
  cgRegistrations: number;
  daRegistrations: number;
  carDeals: number;
  featureFlags: number;
  leadsVoAgents: number;
  persons: number;
}

export interface AgencyOrganization {
  id: number;
  name: string;
  legalEntity: string | null;
  address: string | null;
  postCode: string | null;
  town: string | null;
  country: string | null;
  tel: string | null;
  email: string | null;
  siret: string | null;
  franchiseId: string | null;
  personName: string | null;
  personEmail: string | null;
  updatedAt: string | null;
  deletedAt: string | null;
}

export interface AgencyAccount {
  id: number;
  orgId: number | null;
  orgName: string | null;
  orgEmail: string | null;
  orgNetwork: string | null;
  firebaseId: string | null;
  firestore: { exists: boolean; role: string | null; idPipedrive: number | null; readError: string | null };
  /** `firebase_accounts` et Firestore ne pointent pas la même organisation. Firestore fait foi. */
  isLinkDiverging: boolean;
}

export interface SiblingOrganization extends AgencyOrganization {
  matchReason: 'email' | 'siren';
  counts: AgencyAttachmentCounts;
  accountCount: number;
}

/** Ligne du journal `organization_entity_changes`. */
export interface EntityChange {
  id: number;
  effective_date: string;
  reason: string;
  previous_name: string | null;
  previous_siret: string | null;
  new_name: string;
  new_siret: string;
  pipedrive_updated: number;
  qonto_updated: number;
  propagation_errors: string | null;
  performed_by: string | null;
}

export interface AgencyProfile {
  organization: AgencyOrganization;
  counts: AgencyAttachmentCounts;
  persons: { id: number; name: string; email: string | null; phone: string | null; isPrimary: boolean }[];
  accounts: AgencyAccount[];
  accountsHoldingEmail: AgencyAccount[];
  siblings: SiblingOrganization[];
  entityChanges: EntityChange[];
  qonto: { clientId: string | null; invoicingCadence: string | null; mappedToFranchise: boolean };
}

export const useAgencyProfile = (orgId: string | undefined) => {
  const [profile, setProfile] = useState<AgencyProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * `silent` : rafraîchit SANS repasser la page en état de chargement.
   *
   * Un refetch bruyant démonte tout l'arbre, donc la modale de changement
   * d'entité et le compte rendu de propagation qu'elle venait d'afficher.
   * L'opérateur ne voyait alors jamais qu'une bascule était partielle.
   */
  const fetchProfile = useCallback(async (options?: { silent?: boolean }) => {
    if (!orgId) return;
    if (!options?.silent) setIsLoading(true);
    setError(null);
    try {
      const response = await makeAuthenticatedApiCall<{ success: boolean; data: AgencyProfile; error?: string }>(
        'get',
        `/api/agency-profile/${orgId}`,
      );
      if (response.data?.success) {
        setProfile(response.data.data);
      } else {
        setError(response.data?.error ?? 'Fiche agence indisponible.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Fiche agence indisponible.');
    } finally {
      setIsLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  return { profile, isLoading, error, refetch: () => fetchProfile({ silent: true }) };
};
